import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { tendersAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Zap, BookMarked, Building2, ArrowRight } from "lucide-react";
import toast from "react-hot-toast";

// Demo tenders shown blurred when no company profile
const DEMO_TENDERS = [
  { id: 0, title: "Supply of Heavy-Duty Construction Equipment — Phase 4", agency: "Niger Delta Development Commission", state: "Rivers", category: "Works", contract_value_formatted: "₦485,000,000", days_remaining: 12, urgency: "urgent",   match_score: 91 },
  { id: 1, title: "Provision of IT Infrastructure and Network Services", agency: "Central Bank of Nigeria",             state: "Federal", category: "ICT",   contract_value_formatted: "₦120,000,000", days_remaining: 18, urgency: "soon",     match_score: 84 },
  { id: 2, title: "Rehabilitation of 3 General Hospitals — Package A",   agency: "Rivers State Government",            state: "Rivers", category: "Works", contract_value_formatted: "₦750,000,000", days_remaining: 7,  urgency: "critical", match_score: 78 },
  { id: 3, title: "Annual Maintenance of Production Facilities",          agency: "Shell Petroleum Development Company", state: "Delta",  category: "Oil & Gas", contract_value_formatted: "₦95,000,000", days_remaining: 24, urgency: "soon",  match_score: 73 },
];

function MatchCard({ tender, blurred = false }) {
  const navigate   = useNavigate();
  const score      = Math.round(tender.match_score || 0);
  const scoreColor = score >= 70 ? "var(--accent-green)" : score >= 40 ? "var(--accent-orange)" : "var(--accent-blue)";

  return (
    <div
      className="card"
      style={{ cursor: blurred ? "default" : "pointer", borderLeft: `3px solid ${scoreColor}`, filter: blurred ? "blur(4px)" : "none", userSelect: blurred ? "none" : "auto", pointerEvents: blurred ? "none" : "auto", transition: "filter 0.2s" }}
      onClick={() => !blurred && navigate(`/tenders/${tender.id}`)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px", flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ background: "rgba(0,212,161,0.1)", color: scoreColor, padding: "3px 10px", borderRadius: "100px", fontSize: "12px", fontWeight: "700" }}>
              ⚡ {score}% match
            </span>
            <span className={`badge badge-${tender.urgency}`}>{tender.days_remaining}d left</span>
            <span className="badge badge-open">{tender.category}</span>
          </div>
          <h3 style={{ fontSize: "15px", fontWeight: "600", marginBottom: "4px" }}>{tender.title}</h3>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{tender.agency} · {tender.state}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginLeft: "16px" }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "17px", fontWeight: "700", color: "var(--accent-green)", fontFamily: "Syne" }}>{tender.contract_value_formatted}</div>
            <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>Contract Value</div>
          </div>
          <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: `conic-gradient(${scoreColor} ${score * 3.6}deg, var(--bg-secondary) 0deg)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "var(--bg-card)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", color: scoreColor }}>
              {score}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Blurred preview + overlay when no company profile
function BlurredPreview({ navigate }) {
  return (
    <div style={{ position: "relative" }}>
      {/* Blurred demo cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", pointerEvents: "none" }}>
        {DEMO_TENDERS.map(t => <MatchCard key={t.id} tender={t} blurred />)}
      </div>

      {/* Overlay */}
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px", background: "linear-gradient(to bottom, rgba(8,13,26,0.5) 0%, rgba(8,13,26,0.92) 40%)" }}>
        <div style={{ width: "72px", height: "72px", borderRadius: "20px", background: "rgba(0,212,161,0.08)", border: "1px solid rgba(0,212,161,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "20px" }}>
          <Building2 size={30} color="var(--accent-green)" />
        </div>
        <div style={{ fontSize: "11px", fontWeight: "800", color: "var(--accent-green)", letterSpacing: "2px", textTransform: "uppercase", marginBottom: "10px" }}>Company Profile Required</div>
        <h2 style={{ fontSize: "22px", fontWeight: "800", textAlign: "center", marginBottom: "12px", color: "var(--text-primary)" }}>Unlock your AI matches</h2>
        <p style={{ fontSize: "14px", color: "var(--text-secondary)", maxWidth: "380px", lineHeight: "1.7", textAlign: "center", marginBottom: "28px" }}>
          ProTrackNG matched <strong style={{ color: "var(--accent-green)" }}>4 tenders above</strong> to a sample profile. Complete your company profile to see tenders matched specifically to your industry, state, and contract size.
        </p>
        <button
          onClick={() => navigate("/companies")}
          style={{ display: "flex", alignItems: "center", gap: "8px", padding: "13px 28px", background: "linear-gradient(135deg, #00d4a1, #00b888)", border: "none", borderRadius: "10px", color: "#080d1a", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}
        >
          <Building2 size={16} /> Complete Company Profile <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

export default function Matched() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const hasCompany = !!user?.company_id;

  useEffect(() => {
    if (!hasCompany) { setLoading(false); return; }
    tendersAPI.matched()
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load matches"))
      .finally(() => setLoading(false));
  }, [hasCompany]);

  const handleSave = async (id, e) => {
    e.stopPropagation();
    try {
      const res = await tendersAPI.save(id);
      toast.success(res.data.message);
      setData(prev => ({ ...prev, items: prev.items.map(t => t.id === id ? { ...t, is_saved: res.data.saved } : t) }));
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div>
      <div style={{ marginBottom: "28px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <div style={{ width: "36px", height: "36px", background: "var(--accent-green-dim)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Zap size={18} color="var(--accent-green)" />
          </div>
          <h1 style={{ fontSize: "26px" }}>AI Matched Tenders</h1>
        </div>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
          {loading         ? "Analyzing your profile..."
            : !hasCompany  ? "Set up your company profile to see your personalised matches"
            : `${data?.total_matched || 0} tenders matched to ${data?.company || "your company"}`}
        </p>
      </div>

      {/* No company — show blurred preview */}
      {!hasCompany && <BlurredPreview navigate={navigate} />}

      {/* Loading */}
      {hasCompany && loading && (
        <div style={{ textAlign: "center", padding: "80px" }}>
          <div style={{ width: "36px", height: "36px", border: "3px solid rgba(0,212,161,0.15)", borderTopColor: "var(--accent-green)", borderRadius: "50%", animation: "spin 0.9s linear infinite", margin: "0 auto 16px" }} />
          <style>{"@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
          <p style={{ color: "var(--text-muted)" }}>AI is analysing your company profile...</p>
        </div>
      )}

      {/* No matches */}
      {hasCompany && !loading && data?.items?.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "60px" }}>
          <Zap size={40} color="var(--text-muted)" style={{ marginBottom: "16px" }} />
          <h3 style={{ marginBottom: "8px" }}>No matches yet</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "24px" }}>
            Complete your company profile with your industry, preferred categories, and contract capacity to get AI-powered matches.
          </p>
          <button className="btn btn-primary" onClick={() => navigate("/companies")}>Complete Company Profile</button>
        </div>
      )}

      {/* Matches */}
      {hasCompany && !loading && data?.items?.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {data.items.map(tender => (
            <div key={tender.id} style={{ position: "relative" }}>
              <MatchCard tender={tender} />
              {/* Save button overlaid cleanly */}
              <button
                className="btn btn-ghost"
                style={{ position: "absolute", top: "16px", right: "16px", padding: "6px 10px", zIndex: 2 }}
                onClick={e => handleSave(tender.id, e)}
              >
                <BookMarked size={14} color={tender.is_saved ? "var(--accent-green)" : undefined} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
